import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Map, List as ImmList, fromJS } from 'immutable';
import _ from 'lodash';

import withRouter from '@utils/wrappers/WithRouter';
import WithAllPropsApp from '@utils/wrappers/WithAllPropsApp';
import { useTranslation, withTranslation } from 'react-i18next';

import App from '@gral/app';
import k from '@gral/constants';
import h from '@utils/helpers';
import act from '@flux/actions/actView';
import { omitKeys, getSetting } from '@utils/helpers';
import { Loading } from '@components/Misc';
import Tooltip from '@components/Outputs/Tooltip';
import logger from '@gral/log';
import Error403 from '@components/Errors/403';
import { Grid, Modal } from '@ts/storybook';

import Toolbar from './00-toolbar';
import Footer from './02-footer';
import TableHeader from './01-header';
import TableSettings from './TableSettings';
import { Icon } from '@ts/storybook';

import { getCSVDownloadLimit } from '@gral/config.js';

import {
  TableContainer,
  Popover,
  Table,
  Collapse,
  List,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Divider,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/system';
import { makeStyles } from '@mui/styles';

import { getMenuItems } from '@gral/config';
import { getFilterNeeds, buildAdvancedSearchState, getPredefinedFilter, getPredefinedColumns } from './00-advancedSearchUtils';

import NeedsManager from '@utils/NeedsManager';
import { useSetState } from 'react-use';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { EAclAction } from '@ts/ui-models';

const CSV_DOWNLOAD_LIMIT = getCSVDownloadLimit();
const WARNING_TABLES = ['location'];

const useStyles = makeStyles<Theme, { isEntitySelector: boolean; theme: Theme }>(() => {
  return {
    row: (props: any) => ({
      height: '55px',
      cursor: props.isEntitySelector ? 'pointer' : 'default',
      position: 'relative',
      overflow: 'hidden',
    }),
    btnContainer: (props: any) => ({
      position: 'absolute',
      alignItems: 'center',
      backgroundColor: props.theme.palette.primary.main,
      height: '55px',
      padding: '6px 6px 2px 6px',
      display: 'flex',
      justifyContent: 'space-between',
      opacity: 0,
    }),
    stickyCell: {
      backgroundColor: 'white',
      position: 'sticky',
      right: 0,
    },
    divider: {
      width: '1px',
      height: '40px',
      marginLeft: '10px',
      marginRight: '10px',
      backgroundColor: 'black',
    },
    list: (props: any) => ({
      backgroundColor: props.theme.palette.primary.main,
      color: 'white',
    }),
  };
});

const TableV3 = props => {
  const {
    findAndCount = true, // For pagination
    showFilterDesc = true, // For see the chips with the filters *no used* (always true)
    isModal = false, //See if the tableV3 is in modal
    isNeededConfirmation = true, // For delete confirmation
  } = props;
  const theme = useTheme();
  const classStyles = useStyles({ isEntitySelector: props.isEntitySelector, theme });
  const log = logger(`TableV3-${props.title}`);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  let refreshInterval;
  const _rowSelected: any = useRef(null);
  const componentSettings = useAppSelector(
    state => state.global.settings?.componentSettings[props.componentId] ?? state.global.settings?.componentSettings['default']
  );

  const listActions = [
    {
      label: 'View',
      icon: 'visibility',
      onClick: () => handleClickView(),
      disabled: () => !Boolean(shouldAllowView()),
    },
    {
      label: 'Edit',
      icon: 'edit',
      onClick: () => handleClickEdit(),
      disabled: () => !Boolean(shouldAllowEdit()),
    },
  ];

  //#region Functions before state for state
  const getRefreshInfo = () => {
    const defaultRefresh = {
      isEnabled: true,
      autoRefresh: false,
      minInterval: 10,
      refreshTime: 60,
    };
    return {
      ...defaultRefresh,
    };
  };
  //#region ComponentSettings
  const getInitSort = () => {
    const sortColumn = componentSettings?.initialSort;
    return {
      sortColumn: sortColumn && sortColumn.column,
      sortOrder: sortColumn && sortColumn.order,
      sortableColumns: componentSettings?.sortableColumns ?? [],
    };
  };
  //#endregion
  //#endregion

  const [state, setState] = useSetState<any>({
    title: props.title,
    pagination: {
      activePage: 1,
      itemsPerPage: getSetting('rowsPerPage') || 5,
      total: 0,
    },
    sort: getInitSort(),

    quickfindWord: props.quickFindInit || '',

    //AdvancedSearch
    isAdvancedSearchInit: false, //Check if the route filter is fetched
    lockAdvancedSearch: true, //Check if there ara a advancedSearch component
    advancedSearch: Map<any>({}),
    advancedSearchAux: Map({}),

    refresh: getRefreshInfo(), //Check to refresh page?

    //Action
    showDeleteConfirmation: false, //Modal
    //Settings
    showAdvancedSearch: false, //Modal
    showTableSettings: false, //Modal
    tableSettings: {
      refresh: getRefreshInfo(),
      columnsToShow: props.columns,
      columnsData: props.columns.map(column => ({
        columnId: column.logicalId || column.name,
      })),
    },

    //QuickAccess
    rowSelected: null,
    listExtraActions: props.columns.find(c => c.name === 'actionQuickAccess')?.listExtraActions, //List actions from each component
    anchorRefs: null, //For popOverModal
  });

  //_pkColumn = _.find(props.columns, c => c.name == props.pkColumn);
  const _dataPath = props.itemsDataPath || props.componentId || 'itemsPage';

  const [isFirstFetchDone, setFirstFetchDone] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [listRows, setListRows] = useState<ImmList<any>>(ImmList());
  const [listColumns, setListColumns] = useState<any[]>([]);
  const [filterNeeds, setFilterNeeds] = useState<any[]>([]); //{dataPath: '', data: {},url: '',roleId,organization}
  const previousQueryRef = useRef<any>(null);

  const [predefinedFilter, setPredefinedFilter] = useState<any>({}); //{ "itemType": "TKTIO_ITTP_PAV"}

  //#region Functions
  const setTitle = () => {
    const currentMenuItem = getMenuItems()?.find(menuItem => menuItem.route == props.transferPropsRoute.path);

    setState({ title: _.get(currentMenuItem, 'name') ?? props.title });
  };

  //#region Toolbar
  const clearAutoRefresh = () => {
    if (refreshInterval) clearInterval(refreshInterval);
  };

  const initPredefinedColumns = () => {
    let listColumns = props.columns;

    let route = props.transferPropsRoute;
    let columns = getPredefinedColumns(route?.query?.column);
    if (columns && columns.columns[0] != null) {
      listColumns = [];
      columns.columns.map(column => {
        listColumns = listColumns.concat([]);
      });
    }
    //Add column moreActions
    listColumns = listColumns.concat(addColumnMoreActions());

    listColumns.map((column, idx) => {
      column.idx = idx;
    });
    setListColumns(listColumns);
  };

  /**
   * Initializes the advanced search functionality.
   * if isAdvancedSearchInit is true, it means that the route filter is fetched
   */
  const initAdvancedSearch = () => {
    const newState: any = { isAdvancedSearchInit: true };
    if (props.advancedSearchComponent) {
      newState.lockAdvancedSearch = false;
      if (shouldInitPredefinedFilters()) {
        Object.assign(newState, initPredefinedFilters());
      }
      if (props.initAdvancedSearch) {
        newState.advancedSearch = fromJS(props.initAdvancedSearch);
      }
    }

    setState(newState);
  };
  /**
   * Initialize predefined filters based on the transferPropsRoute.
   *
   * @return {Object} The new state after initializing the predefined filters.
   */
  const initPredefinedFilters = () => {
    // get filter data (currently from same menu item)
    const newState: any = {};
    const route = props.transferPropsRoute;

    const inPredefinedFilter = getPredefinedFilter(route?.query?.filter) || route?.query;

    if (!inPredefinedFilter) {
      dispatch(
        globalActions.notification(
          `${t('Warning: Missing filter configuration')}: ${route?.query?.filter}. ${t('Showing unfiltered results')}.`
        )
      );
      return newState;
    }

    setPredefinedFilter(inPredefinedFilter);

    // we will need to fetch the entities for entitySelectors...
    const filterNeeds = getFilterNeeds(inPredefinedFilter);

    setFilterNeeds(filterNeeds);

    if (_.isEmpty(filterNeeds)) {
      let initialAdvancedSearch = buildAdvancedSearchState(inPredefinedFilter);

      newState.advancedSearch = fromJS(initialAdvancedSearch);
      newState.isAdvancedSearchInit = true;
    } else {
      let needsManager = new NeedsManager(props.data, filterNeeds);
      needsManager.fetchNeeds();
      newState.isAdvancedSearchInit = false;
    }

    return newState;
  };

  const shouldInitPredefinedFilters = () => {
    return (
      (props.transferPropsRoute.query?.filter || (props.predefinedFilter && Object.keys(props.predefinedFilter).length > 0)) &&
      !isModal &&
      !props.inheritedProps.isModal
    );
  };
  //#endregion

  //#region Actions Table
  const addColumnMoreActions = () => {
    return {
      fnLabel: () => null,
      idx: 100,
      name: 'moreActions',
      rawValue: o => {
        return '';
      },
      render: o => {
        return renderButtonsActionRow(o, o.get('logicalId'));
      },
    };
  };
  //#endregion

  //#region Fetch DATA
  const fetch = ({ url, dataPath, data }) => {
    act.fetch({ url, dataPath, data });
  };
  const fetchPage = () => {
    act.markAsStale(_dataPath);
    let fetchData = getPageFetchData();
    log.debug('Fetch page', {
      attach: JSON.stringify(fetchData, null, 2),
    });

    fetch({
      url: props.itemsFetchUrl,
      dataPath: _dataPath,
      data: fetchData,
    });
  };
  //const debouncedFetch = _.debounce(fetchPage, 450);
  //#region FILTER / WHERE
  const getPageFetchData = () => {
    const { itemsPerPage, activePage } = state.pagination;
    let fetchData = getMainFetchData();

    // little hack for no-count case
    // get 1 item more in order to know if there are more items.
    const limit = findAndCount ? itemsPerPage : itemsPerPage + 1;
    const offset = (activePage - 1) * itemsPerPage;
    fetchData = _.assign(fetchData, {
      limit,
      offset,
      findAndCount,
    });
    return fetchData;
  };

  const getMainFetchData = () => {
    const where = getWhere();
    const filters = getAdvancedSearchExternalFilters();
    const advancedFilters = getAdvancedSearchAdvancedFilters();
    return {
      where: where,
      filters: filters,
      advancedFilters: advancedFilters,
      sortColumn: state.sort.sortColumn,
      sortOrder: state.sort.sortOrder,
    };
  };

  const getWhere = () => {
    const quickFindColumns = componentSettings?.quickFindColumns ?? ['logicalId'];
    let where = { ...props.where, ...props.fetchData?.where };

    let like = {};
    if (isValidQuickfind()) {
      if (quickFindColumns) {
        like = {
          $or: quickFindColumns.map(key => ({
            [key]: { $iLike: `%${state.quickfindWord}%` },
          })),
        };
      }
      where = { ...where, ...like };
    }
    if (state.advancedSearch.count()) {
      where = { ...getAdvancedSearchInternalFilters(), ...where };
    }

    return where;
  };

  const getAdvancedSearchInternalFilters = () => {
    let internalFilters = {};
    state.advancedSearch
      .filter(item => item.get('scope') == 'internal')
      .forEach(item => {
        const searchAttr = item.getIn(['search', 'attr']);
        const searchValue = item.getIn(['search', 'value']);
        internalFilters[searchAttr] = searchValue;
      });
    return internalFilters;
  };

  const getAdvancedSearchExternalFilters = () => {
    let externalFilters = {};
    if (state.advancedSearch.count()) {
      state.advancedSearch
        .filter(item => item.get('scope') == 'external')
        .forEach((item, key) => {
          //get first part of "the alias path" eg: wipPart.serialNumber -> wipPart
          let splittedKey = key.split('.')[0];
          if (!externalFilters[splittedKey]) {
            externalFilters[splittedKey] = {};
          }
          let searchAttr, searchValue;
          //special cases...
          if (splittedKey == 'indicators') {
            searchAttr = '$or';
            searchValue = [];
            item.get('search').forEach(indicator => {
              const valueQuery = {};
              valueQuery[`$${indicator.getIn(['operation', 'type'])}`] = indicator.getIn(['operation', 'value']);
              searchValue.push({
                indicatorTypeId: indicator.get('logicalId'),
                'props.value': valueQuery,
              });
            });
          } else if (item.getIn(['search', 'attr']) == '$and') {
            searchAttr = item.getIn(['search', 'attr']);
            const fixedAnd: any = [];
            const listItemValues = item.getIn(['search', 'value']) || [];
            listItemValues.forEach((whereVal, whereKey) => {
              let fixedWhereItem: any = {};
              fixedWhereItem[whereKey] = whereVal;
              fixedAnd.push(fixedWhereItem);
            });
            searchValue = fixedAnd;
          } else {
            searchAttr = item.getIn(['search', 'attr']);
            searchValue = item.getIn(['search', 'value']);
          }
          externalFilters[splittedKey][searchAttr] = searchValue;
        });
    }
    return { ...props.predefinedFilter, ...props.fetchData?.filters, ...props.filters, ...externalFilters };
  };

  const getAdvancedSearchAdvancedFilters = () => {
    let advancedFilters = {};
    if (state.advancedSearch.count()) {
      state.advancedSearch
        .filter(item => item.get('scope') == 'custom')
        .forEach((item, key) => {
          const searchValue = item.getIn(['search']);
          advancedFilters[key] = searchValue;
        });
    }
    return advancedFilters;
  };

  const isValidQuickfind = () => {
    return state.quickfindWord && !_.isEmpty(state.quickfindWord);
  };

  const checkParamsRouteToFetch = () => {
    if (!state.isAdvancedSearchInit && filterNeeds.length > 0) {
      // check all fetched needs arrived
      let allIdle = true;
      filterNeeds.forEach(need => {
        if (!props.data.get(need.dataPath) || props.data.getIn([need.dataPath, 'state']) == 'SYNCING') {
          allIdle = false;
        }
      });
      if (allIdle) {
        // all needs arrived, build advanced search and set init to true
        let entities = {};
        let notFound: any = [];
        filterNeeds.forEach(need => {
          let entity = props.data.getIn([need.dataPath, 'data', 0]);
          // remove uuid from path
          let key = need.dataPath.slice(0, need.dataPath.lastIndexOf('_'));
          if (entity) {
            entities[key] = entity;
            // if itemType set in preCreation
            if (key == `predefinedFilter_itemType`)
              act.setDataPath('preinformedValues', {
                elementTypeId: entity.get('logicalId'),
              });
          } else notFound.push(need);
        });
        if (notFound.length) {
          dispatch(
            globalActions.notification(`${t('Warning: Bad filter configuration')}.
            ${t('Some filter entities were not found')}.
            ${t('Showing partially filtered results')}`)
          );
        }
        entities = fromJS(entities);

        let initialAdvancedSearch = buildAdvancedSearchState(predefinedFilter, entities);

        const advancedSearch = fromJS({
          ...state.advancedSearch.toJS(),
          ...initialAdvancedSearch,
        });

        setState({
          isAdvancedSearchInit: true,
          advancedSearch,
        });
      }
    }
  };
  //#endregion

  //#region DOWNLOAD CSV
  const fetchCSV = () => {
    let fetchData = getCSVFetchData();
    log.debug('Fetch page CSV', {
      attach: JSON.stringify(fetchData, null, 2),
    });
    fetch({
      url: props.itemsFetchUrl,
      dataPath: `CSVDownload`,
      data: fetchData,
    });
  };
  const getCSVFetchData = () => {
    let fetchData = getMainFetchData();
    fetchData = _.assign(fetchData, {
      limit: CSV_DOWNLOAD_LIMIT,
    });
    return fetchData;
  };
  //#endregion

  // Data processing methods
  const getStoreErrors = store => {
    let errors = null;
    if (store.getIn([_dataPath, 'state']) == 'IDLE') {
      errors = store.getIn([_dataPath, 'errors']);
    }
    return errors;
  };

  const getStoreData = store => {
    // could be a generic function
    let storeData: any = {};

    if (store.getIn([_dataPath, 'state']) == 'IDLE') {
      if (findAndCount) {
        storeData.listRows = store.getIn([_dataPath, 'data', 'rows']);
        storeData.total = store.getIn([_dataPath, 'data', 'count']);
      } else {
        storeData.listRows = store.getIn([_dataPath, 'data']);
        storeData.total = null;
      }
    }

    return storeData;
  };

  const preprocessRow = (rows: ImmList<any>) => {
    // Adds the colValues array in the row: values that goes to columns

    /* if (_shouldRemoveControlRow(rows)) {
      // Remove "control" last item if no count.
      newRows = rows.pop();
    } */

    const addAllAtributesNecessary = (rows: ImmList<any>) => {
      return rows.map((row, index) => {
        let newRow = row;
        const colValues = listColumns.map(column => getRawValue(row, column));

        if (!row.get('logicalId')) {
          newRow = newRow.set('logicalId', index);
        }
        if (props.collapsableComponent) {
          newRow = newRow.set('isCollapsable', false);
        }
        return newRow.set('_colValues', colValues);
      });
    };

    return addAllAtributesNecessary(rows);
  };

  /*   const shouldRemoveControlRow = rows => {
    const { findAndCount } = props;
    return !findAndCount && rows.size > state.pagination.itemsPerPage;
  }; */

  const getRawValue = (row, column) => {
    // Get the value of the row using the function defined in the column
    const fn = column.rawValue || (o => o.get(column.name));
    return fn(row);
  };

  //#region CRUD
  const isReadyForCrud = () => {
    return props.editUrl || props.navContext;
  };

  const shouldAllowCreate = () => {
    return App.cxStore.checkPermission(props.aclId, EAclAction.CREATE);
  };

  const shouldAllowView = () => {
    return App.cxStore.checkPermission(props.aclId, EAclAction.READ) && isReadyForCrud();
  };

  const checkRestictionEdit = () => {
    if (!props.restrictionEdit) return true;
    return _rowSelected.current?.toJS()[props.restrictionEdit.field] !== props.restrictionEdit.value;
  };

  const shouldAllowEdit = () => {
    return isReadyForCrud() && App.cxStore.checkPermission(props.aclId, EAclAction.EDIT) && checkRestictionEdit();
  };

  const checkRestictionDelete = () => {
    if (!props.restrictionDelete) return true;
    return _rowSelected.current?.toJS()[props.restrictionDelete.field] !== props.restrictionDelete.value;
  };

  const shouldAllowDelete = () => {
    return isReadyForCrud() && App.cxStore.checkPermission(props.aclId, EAclAction.DECOMMISSION) && checkRestictionDelete();
  };
  //#endregion
  //#endregion

  //#endregion

  //#region Handlers
  /*   
  Maybe for check de column for nothing
  const isCheckedRow = row => {
    return props.selected.has(row.get('logicalId'));
  };  _handleCheckClick(row) {
    let newSelected;
    if (_isCheckedRow(row)) {
      //remove from selected
      newSelected = props.selected.delete(row.get('logicalId'));
    } else {
      //add to selected
      newSelected = props.selected.set(row.get('logicalId'), row);
    }
    props.onChangeSelected(newSelected);
  }

    const areAllChecked = () =>
    listRows.reduce((accum, row) => {
      return accum && _isCheckedRow(row);
    }, true);

  const handleCheckAllClick = () => {
    let newSelected = props.selected;

    if (_areAllChecked()) {
      // deselect all
      listRows.forEach(row => {
        newSelected = newSelected.delete(row.get('logicalId'));
      });
    } else {
      // select all
      listRows.forEach(row => {
        if (_isCheckedRow(row) == false) {
          newSelected = newSelected.set(row.get('logicalId'), row);
        }
      });
    }

    props.onChangeSelected(newSelected);
  }; */

  const handleClickRow = row => ev => {
    changeSelected(row);
  };

  /**
   * Handles the hover event on a row by updating the opacity of the corresponding element.
   *
   * @param {Object} row - The row being hovered over.
   * @return {Function} The event handler function.
   */
  const onHoverRow = row => ev => {
    const elements = ['row', 'listaction', 'cell'];
    if (_rowSelected.current) {
      elements.forEach(el => {
        const oldElement = document.getElementById(_rowSelected.current.get('logicalId') + `-${el}`);
        if (oldElement) {
          if (el === 'row' || el === 'cell') {
            oldElement.style.backgroundColor = 'white';
          } else if (el === 'listaction') {
            oldElement.style.opacity = '0';
          }
        }
      });
    }
    if (row) {
      elements.forEach(el => {
        const element = document.getElementById(row.get('logicalId') + `-${el}`);
        if (element) {
          if (el === 'row' || el === 'cell') {
            element.style.backgroundColor = k.gui.colors.lighterGray;
          } else if (el === 'listaction') {
            element.style.opacity = '1';
          }
        }
      });
    }

    _rowSelected.current = row;
  };

  const onClickCollapsable = rowId => {
    const updatedRows = listRows.map(row => {
      if (rowId === 'all') return row.set('isCollapsable', false);
      if (row.get('logicalId') === rowId) {
        return row.set('isCollapsable', !row.get('isCollapsable'));
      }
      return row;
    });

    setListRows(updatedRows);
  };

  const changeSelected = row => {
    if (row) _rowSelected.current = row;

    setState({ rowSelected: row });
    if (props.fnChange) props.fnChange(row);
    if (props.onChange) props.onChange(row);
  };

  // Header handlers
  const handleColumnHeadClick = colName => {
    const { sortableColumns, sortColumn, sortOrder } = state.sort;
    const sortableColumn = sortableColumns.find(sc => sc.field === colName);
    if (!sortableColumn) return;

    const newSortColumn = sortableColumn.sortColumn;
    const newSortOrder =
      sortableColumn.sortOrder && sortColumn !== sortableColumn.sortColumn
        ? sortableColumn.sortOrder
        : sortOrder === 'DESC'
        ? 'ASC'
        : 'DESC';
    setState({
      sort: { ...state.sort, sortColumn: newSortColumn, sortOrder: newSortOrder },
    });
  };

  //#region Toolbar handlers
  const onClickNew = () => {
    prepareDatapath();
    /* let nextNavContext;
    if (props.navContext) {
      nextNavContext = NavigatorController.getNextNavContext(props.navContext, new NavAction(k.actionTypes.CLICK_NEW));
    } */
    ///const url = nextNavContext ? nextNavContext.getRoute() : `${props.editUrl}/new`;
    const url = `${props.editUrl}/new`;
    const { navigate } = props;
    navigate(url);
  };

  const handleAutoRefresh = () => {
    const { refresh } = state;
    const { autoRefresh, refreshTime } = refresh;
    const newAutoRefreshState = !autoRefresh;
    if (newAutoRefreshState) {
      refreshInterval = setInterval(fetchPage, refreshTime * 1000);
    } else {
      clearAutoRefresh();
    }
    setState({
      refresh: { ...refresh, autoRefresh: newAutoRefreshState },
    });
  };

  const handleChangeQuickFind = ev => {
    setState({ quickfindWord: ev.target.value, pagination: { ...state.pagination, activePage: 1 } });
  };

  const handleClickQuickFind = () => {
    setState({ pagination: { ...state.pagination, activePage: 1 } });
  };

  const clearFilters = () => {
    setState({ quickfindWord: '', advancedSearch: Map({}), pagination: { ...state.pagination, activePage: 1 } });
  };

  const prepareDatapath = () => {
    //This function prepare the data for EditPage it will remove
    if (!props.editDataPath) return;
    const { defaultCreationProperties: propsInitValues, editDataPath } = props;

    let initialValues = propsInitValues ? Map(propsInitValues) : Map({});
    if (_rowSelected.current) {
      initialValues = initialValues.merge(omitKeys(_rowSelected.current));
    }

    act.setDataPath(editDataPath, initialValues);
  };

  const handleAdvancedSearchClick = () => {
    setState({ showAdvancedSearch: true });
  };

  const handleTableSettingsClick = () => {
    // Set as tableSettings the current settings and then
    // modify settings in a different object before saving on confirm
    setState({
      showTableSettings: true,
    });
  };

  const handleDeleteFilter = key => () => {
    const advancedSearch = state.advancedSearch.delete(key);
    setState({ advancedSearch, pagination: { ...state.pagination, activePage: 1 } });
    // we may lift the entire advancesSearch state up
    if (props.onChangeAdvancedSearch) props.onChangeAdvancedSearch(advancedSearch);
  };

  //#region AdvancedSearch
  const handleConfirmAdvancedSearch = () => {
    // set advancedSearchAux as advancedSearch
    setState({
      showAdvancedSearch: false,
      advancedSearch: state.advancedSearchAux,
      pagination: { ...state.pagination, activePage: 1 },
    });
  };
  const handleAdvancedSearchAuxChange = advancedSearchAux => {
    setState({ advancedSearchAux });
    // we may lift the entire advancesSearch state up
    if (props.onChangeAdvancedSearch) props.onChangeAdvancedSearch(advancedSearchAux);
  };
  //#endregion

  //#region TableSettings
  const handleConfirmTableSettings = () => {
    const { refresh, tableSettings } = state;
    const { refreshTime: newRefreshTime, autoRefresh: newAutoRefreshState } = tableSettings.refresh;
    if (newAutoRefreshState && newRefreshTime !== refresh.refreshTime) {
      clearAutoRefresh();
      refreshInterval = setInterval(fetchPage, newRefreshTime * 1000);
    } else if (!newAutoRefreshState && newAutoRefreshState !== refresh.autoRefresh) {
      clearAutoRefresh();
    }
    setState({
      showTableSettings: false,
      refresh: {
        ...refresh,
        ...tableSettings.refresh,
      },
      columnsData: [...tableSettings.columnsData],
    });
  };

  const handleAutoRefreshTableSettings = () => {
    const { tableSettings } = state;
    setState({
      tableSettings: {
        ...tableSettings,
        refresh: {
          ...tableSettings.refresh,
          autoRefresh: !tableSettings.refresh.autoRefresh,
        },
      },
    });
  };

  const onBlurRefreshTime = () => {
    const { tableSettings } = state;
    const { refresh } = tableSettings;
    const { refreshTime: newRefreshTime, minInterval } = refresh;
    if (newRefreshTime < minInterval) {
      setState({
        tableSettings: {
          ...tableSettings,
          refresh: { ...refresh, refreshTime: minInterval },
        },
      });
    }
  };

  //#region TableSettings - ShowColumns
  /*   const onSelectColumnToShow = column => {
    const { tableSettings } = state;
    const columnId = column.get ? column.get('logicalId') : column.id || column.name;
    const newColumn = {
      uuid: uuid.v4(), // we need this uuid to be able to use it as a unique key when columns belong to same entity (same lid)
      ...(column.toJS ? column.toJS() : column),
      idx: tableSettings.columnsToShow.length,
    };
    setState({
      tableSettings: {
        ...tableSettings,
        columnsToShow: tableSettings.columnsToShow.concat(newColumn),
        columnsData: tableSettings.columnsData.concat({ columnId }),
      },
    });
  };

  const onRemoveColumnToShow = column => {
    const { tableSettings } = state;
    setState({
      tableSettings: {
        ...tableSettings,
        columnsToShow: tableSettings.columnsToShow
          .filter(columnToShow => columnToShow.idx !== column.idx)
          .map((columnToShow, idx) => ({ ...columnToShow, idx })),
        columnsData: tableSettings.columnsData.filter((columnData, idx) => idx !== column.idx),
      },
    });
  }; */
  //#endregion

  const handleChangeRefreshTime = time => {
    const { tableSettings } = state;
    const value = time.target.value;
    setState({
      tableSettings: {
        ...tableSettings,
        refresh: {
          ...tableSettings.refresh,
          refreshTime: value,
        },
      },
    });
  };
  //#endregion

  //#endregion

  //#region Actions table
  const handleClickView = () => {
    let logicalId = props.navigateId
      ? _rowSelected.current?.getIn(props.navigateId)
      : _rowSelected.current?.get('logicalId') || _rowSelected.current?.get('logicalId');
    let url;
    // let nextNavContext;
    if (!logicalId) return;

    /* if (props.navContext) {
      nextNavContext = NavigatorController.getNextNavContext(props.navContext, new NavAction(k.actionTypes.CLICK_VIEW));
    } */

    /* if (nextNavContext) {
      url = nextNavContext.getRoute();
      if (url) url = url.replace(':id', logicalId);
    } else { */
    url = `${props.editUrl}/${logicalId}/view`;
    //}
    const { navigate } = props;
    navigate(url);
  };

  const handleClickEdit = () => {
    let logicalId = props.navigateId
      ? _rowSelected.current.getIn(props.navigateId)
      : _rowSelected.current?.get('logicalId') || _rowSelected.current?.get('logicalId');
    let url;
    //let nextNavContext;
    if (!logicalId) return;

    /* if (props.navContext) {
      nextNavContext = NavigatorController.getNextNavContext(props.navContext, new NavAction(k.actionTypes.CLICK_VIEW));
    } */

    /* if (nextNavContext) {
      url = nextNavContext.getRoute();
      if (url) url = url.replace(':id', logicalId);
    } else { */
    url = `${props.editUrl}/${logicalId}/edit`;
    // }
    const { navigate } = props;
    navigate(url);
  };

  const handleClickDelete = () => {
    // if (isNeededConfirmation) {
    setState({ itemToDelete: _rowSelected.current, showDeleteConfirmation: true });
    // } else {
    // _handleConfirmDelete();
    // }
  };

  const handleConfirmDelete = () => {
    const { editUrl, crudURL } = props;

    const id =
      state.itemToDelete?.get('logicalId') || _rowSelected.current?.get('logicalId') || _rowSelected.current?.get('logicalId');

    if (!id) return;

    //editUrl is a "front-end" route not an api end-point
    const url = crudURL ? `${crudURL}/${id}` : `/api${editUrl}/${id}`;
    let fetchData = getPageFetchData();
    if (props.fetchData) {
      fetchData = _.merge(_.cloneDeep(props.fetchData), fetchData);
    }
    // fetch to do after decom
    let fetch = {
      url: props.itemsFetchUrl,
      dataPath: _dataPath,
      data: fetchData,
    };
    act.decommissionOld({ url, fetch });
    if (state.showDeleteConfirmation) setState({ showDeleteConfirmation: false });
  };
  //#endregion

  //#region Pagination (footer) handlers
  const handleNextPage = ev => {
    setState({ pagination: { ...state.pagination, activePage: state.pagination.activePage + 1 } });
    if (_rowSelected.current) changeSelected(null);
  };

  const handlePreviousPage = ev => {
    setState({ pagination: { ...state.pagination, activePage: state.pagination.activePage - 1 } });
    if (_rowSelected.current) changeSelected(null);
  };

  const handleItemsPerPageChange = value => {
    setState({ pagination: { activePage: 1, itemsPerPage: value } });
    if (_rowSelected.current) changeSelected(null);
  };

  //#endregion

  //#endregion

  //#region Renders
  const render403 = () => {
    const errors: any = getStoreErrors(props.data);
    const show = errors && /unauthorized/i.test(errors.get('_gral'));

    if (show) {
      return (
        <div className="p-4 mb-2.5">
          <Error403 />
        </div>
      );
    }
    return null;
  };

  const shouldRenderInnerLoading = () => {
    // predefinedFilters but advancedSearch state not init yet
    return (predefinedFilter && !state.isAdvancedSearchInit) || isLoading;
  };

  //#region Toolbar
  const renderAdvancedSearch = () => {
    let AdvancedSearchComponent = props.advancedSearchComponent;
    if (!AdvancedSearchComponent || state.lockAdvancedSearch) return;

    return (
      <Modal
        withHeader
        title="Advanced search"
        open={state.showAdvancedSearch}
        labelOk="Confirm"
        labelCancel="Cancel"
        fullWidth
        maxWidth="lg"
        onSubmit={handleConfirmAdvancedSearch}
        onClose={() => {
          setState({ showAdvancedSearch: false });
        }}
      >
        <Grid container p={4}>
          <AdvancedSearchComponent
            app={props.app}
            data={props.data}
            t={t}
            advancedSearch={state.advancedSearch}
            onChangeAdvancedSearch={handleAdvancedSearchAuxChange}
            {...props.advancedSearchComponentProps}
            isModal
          />
        </Grid>
      </Modal>
    );
  };

  const renderTableSettings = () => {
    const TableSettingsComponent = props.tableSettingsComponent || TableSettings;
    const stateProps = _.pick(state, ['tableSettings', 'refresh', 'showTableSettings']);
    const sort = state.sort;
    return (
      <TableSettingsComponent
        data={props.data}
        app={props.app}
        open={state.showTableSettings}
        {...stateProps}
        {...sort}
        onConfirm={handleConfirmTableSettings}
        onCancel={() => {
          setState({ showTableSettings: false });
        }}
        onChangeAutoRefresh={handleAutoRefreshTableSettings}
        onChangeRefreshTime={handleChangeRefreshTime}
        onBlurRefreshTime={onBlurRefreshTime}
        onChangeSortColumn={value => setState({ sort: { ...state.sort, sortColumn: value } })}
        onChangeSortOrder={value => setState({ sort: { ...state.sort, sortOrder: value } })}
        //columns
        columns={props.columns}
        //useColumnsSelector={props.useColumnsSelector}
        //onSelectColumnToShow={onSelectColumnToShow}
        //onRemoveColumnToShow={onRemoveColumnToShow}
      />
    );
  };

  const renderToolbar = () => {
    const { refresh } = state;
    return (
      <Toolbar
        {...props}
        title={state.title}
        withTitle={props.withTitle}
        onClickNew={onClickNew}
        fAllowCreate={props.fAllowCreate ?? shouldAllowCreate()}
        fAllowClearFilters={state.quickfindWord != '' || state.advancedSearch.count()}
        // Quickfind props
        quickfindWord={state.quickfindWord}
        onChangeQuickfind={handleChangeQuickFind}
        onQuickfindButtonClick={handleClickQuickFind}
        onClearFilters={clearFilters}
        // CSVDownloader props
        onDownload={fetchCSV}
        downloadOptions={props.downloadOptions}
        csvDatapath={`CSVDownload`}
        // Advanced search props
        fAllowAdvancedSearch={props.advancedSearchComponent && !state.lockAdvancedSearch}
        onAdvancedSearchClick={handleAdvancedSearchClick}
        onTableSettingsClick={handleTableSettingsClick}
        refreshEnabled={refresh.isEnabled}
        autoRefresh={refresh.autoRefresh}
        onRefresh={fetchPage}
        onAutoRefresh={handleAutoRefresh}
        advancedSearch={state.advancedSearch}
        showFilterDesc={showFilterDesc}
        onDeleteFilter={handleDeleteFilter}
        lockAdvancedSearch={state.lockAdvancedSearch}
      />
    );
  };
  //#endregion

  //#region Table
  const renderTable = () => {
    if (shouldRenderInnerLoading()) return <Loading />;
    let haveFilter = false;
    if (state.advancedSearch.count()) haveFilter = true;
    return (
      <TableContainer
        id="tableV3-container"
        sx={!haveFilter ? { height: 'calc(100% - 120px)' } : { height: 'calc(100% - 163px)' }}
      >
        <Table stickyHeader size="small">
          {renderTableHeader()}
          {renderTableBody()}
        </Table>
      </TableContainer>
    );
  };

  const renderTableHeader = () => {
    const { sortableColumns, sortColumn, sortOrder } = state.sort;

    return (
      <TableHeader
        columns={
          (listColumns && listColumns.filter(c => c.name !== 'actionQuickAccess')) ||
          props.columns.filter(c => c.name !== 'actionQuickAccess')
        }
        rows={listRows}
        //onSelectAllClick={_handleCheckAllClick}
        onColumnHeadClick={handleColumnHeadClick}
        sortableColumns={sortableColumns}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onClickCollapsable={props.collapsableComponent && onClickCollapsable}
      />
    );
  };

  const renderTableBody = () => {
    return (
      <TableBody
        id="tableV3-body"
        sx={{ overflowY: 'auto', position: 'relative' }}
        onMouseLeave={state.anchorRefs || isModal ? () => {} : onHoverRow(null)}
      >
        {listRows.map((row, idxRow) => renderRow(row, idxRow))}
      </TableBody>
    );
  };

  const renderDeleteConfirmation = () => {
    let warningToShow: any = null;
    if (props.showDeleteImplications) warningToShow = props.showDeleteImplications;
    else if (
      _rowSelected.current &&
      _rowSelected.current.get('flavors') &&
      Object.keys(_rowSelected.current.get('flavors').toJS()).some(flavor => WARNING_TABLES.includes(flavor))
    )
      warningToShow = t('This will also erase associations related to the item');

    return (
      <Modal
        withHeader
        open={state.showDeleteConfirmation}
        title={props.confirmDeleteMessage || 'You are about to delete this item — are you sure?'}
        onSubmit={handleConfirmDelete}
        labelOk="Delete"
        warningToShow={warningToShow}
        onClose={() => {
          setState({ showDeleteConfirmation: false });
        }}
        labelCancel="Cancel"
      />
    );
  };

  const renderButtonsActionRow = (row, idxRow) => {
    const columnAction = props.columns.find(c => c.name === 'actionQuickAccess');

    if (!isReadyForCrud() && !state.listExtraActions) return null;

    return (
      <Grid container justifyContent="flex-end">
        {!isModal && (
          <div className="flex items-center">
            <div
              id={row.get('logicalId') + '-listaction'}
              className="flex absolute right-[45px] h-full items-center opacity-0"
              style={{
                backgroundColor: k.gui.colors.lighterGray,
              }}
            >
              {isReadyForCrud() &&
                listActions.map(action => renderIconHover(idxRow, action.label, action.icon, action.onClick, action.disabled))}
              {renderValue(row, columnAction, idxRow)}
            </div>
          </div>
        )}
        {listActions.length > 0 && renderButtonMoreActions(row, 'more_vert')}
      </Grid>
    );
  };

  const renderButtonMoreActions = (row, icon) => {
    return (
      <div className="h-full flex items-center">
        <Tooltip title="More actions">
          <IconButton
            onClick={e => {
              setState({ rowSelected: row, anchorRefs: e.currentTarget });
              e.stopPropagation();
            }}
          >
            <Icon size={20} icon={icon} />
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  const renderIconHover = (id, label, icon, click, disabled) => {
    disabled = disabled();

    if (disabled) return null;
    return (
      <Tooltip key={id + label} title={label} disabled={disabled}>
        <IconButton disabled={disabled} onClick={click}>
          <Icon size={20} color={'inherit'} icon={icon} />
        </IconButton>
      </Tooltip>
    );
  };

  const renderPopoverActions = () => {
    //Change disabled state
    state.listExtraActions &&
      state.listExtraActions.map(action => {
        action.onClick = h.handleDataTableAction(action, h.getDisplayId(_rowSelected.current), _rowSelected.current);
        action.disabled = props.shouldExtraAction ? false : !Boolean(shouldAllowView());
      });

    const dropdownItems = listActions
      .map(action => {
        return { ...action, label: t(action.label), disabled: action.disabled() };
      })
      .concat([
        {
          icon: 'delete',
          label: t('Delete'),
          onClick: () => handleClickDelete(),
          disabled: !Boolean(shouldAllowDelete()),
        },
      ]);

    const isExtraAction = state.listExtraActions && state.listExtraActions.length;

    return (
      state.anchorRefs && (
        <Popover
          open={Boolean(state.anchorRefs)}
          anchorEl={state.anchorRefs}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={() => setState({ anchorRefs: null, rowSelected: null })}
        >
          <div onMouseLeave={() => setState({ anchorRefs: null, rowSelected: null })}>
            {isReadyForCrud() && (
              <List className={classStyles.list}>
                {dropdownItems.map((item, index) => (
                  <ListItemButton className="py-0 bg-[#ffffff47]" key={index} disabled={item.disabled} onClick={item.onClick}>
                    <ListItemIcon>
                      <Icon color="white" size={20} icon={item.icon} />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            )}
            {isExtraAction > 0 && <Divider />}
            {isExtraAction > 0 && (
              <List className={classStyles.list}>
                {state.listExtraActions.map((item, index) => (
                  <ListItemButton className="py-0 bg-[#ffffff47]" key={index} disabled={item.disabled} onClick={item.onClick}>
                    <ListItemIcon>
                      <Icon color="white" size={20} icon={item.icon} />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </div>
        </Popover>
      )
    );
  };

  const renderRow = (row, idxRow) => {
    return (
      <React.Fragment key={idxRow}>
        <TableRow
          id={row.get('logicalId') + '-row'}
          className={`${classStyles.row} `}
          hover
          role="checkbox"
          onClick={!isModal ? () => {} : handleClickRow(row)}
          onMouseEnter={isModal ? () => {} : onHoverRow(row)}
        >
          {props.collapsableComponent && (
            <TableCell sx={{ width: '65px', borderBottom: idxRow === listRows.count() - 1 ? 'none' : '' }}>
              <IconButton aria-label="expand row" size="small" onClick={() => onClickCollapsable(row.get('logicalId'))}>
                <Icon icon={row.get('isCollapsable') ? 'KeyboardArrowDown' : 'KeyboardArrowUp'} />
              </IconButton>
            </TableCell>
          )}
          {listColumns
            .filter(c => c.name !== 'actionQuickAccess')
            .map((column, idxCol) => {
              return renderCell(row, column, idxCol, idxRow);
            })}
        </TableRow>
        {props.collapsableComponent && renderCollapsable(row, idxRow)}
      </React.Fragment>
    );
  };

  const renderCell = (row, column, idxCol, idxRow) => {
    if (column.fHidden) return null;

    let rtl = props.lng == 'ar';
    let style: any = idxRow === listRows.count() - 1 ? { borderBottom: 'none' } : {};
    if (rtl) style = { ...style, textAlign: 'right' };
    const isColumnMoreActions = column.name === 'moreActions' && !isModal;

    return (
      <TableCell
        id={isColumnMoreActions ? row.get('logicalId') + '-cell' : ''}
        key={idxCol}
        className={isColumnMoreActions ? classStyles.stickyCell : ''}
        style={{ ...style }}
      >
        {renderValue(row, column, idxRow)}
      </TableCell>
    );
  };

  const renderValue = (row, column, idxRow) => {
    if (!column) return;

    // Returns the raw value for the column, if the column has a render method
    // calls the render method.
    let fn, rawValue, ref, ref1;
    rawValue = row.get('_colValues')[column.idx];

    if (rawValue == null) return;

    fn = (ref = column.render) != null ? ref : () => rawValue;
    return (ref1 = fn(row, rawValue, idxRow)) != null ? ref1 : null;
  };
  //#endregion

  //#region Footer
  const renderFooterPagination = () => {
    const { total } = state.pagination;
    const rowsLength = listRows.count();
    const { activePage, itemsPerPage } = state.pagination;
    //if (!props.findAndCount) return;
    return (
      <Footer
        isLoading={isLoading}
        // pagination props
        onNextPage={handleNextPage}
        onPrevPage={handlePreviousPage}
        activePage={activePage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        numRows={total}
        rowsLength={rowsLength}
        // options props
        noStickyFooter={props.noStickyFooter}
      />
    );
  };
  //#endregion

  //#region Collapsable
  const renderCollapsable = (row, idxRow) => {
    return (
      <TableRow>
        <TableCell className="pl-10" sx={{ borderBottom: row.get('isCollapsable') ? '' : 'none' }} colSpan={listColumns.length}>
          <Collapse in={row.get('isCollapsable')} timeout="auto" unmountOnExit>
            {props.collapsableComponent(row)}
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };
  //#endregion
  //#endregion

  //#region Effects
  const effectInitTableV3 = (deps: React.DependencyList) => {
    useEffect(() => {
      const { isEnabled: refreshEnabled, refreshTime, autoRefresh } = state.refresh;
      setTitle();
      initPredefinedColumns();

      if (refreshEnabled && autoRefresh) {
        refreshInterval = setInterval(fetchPage, refreshTime * 1000);
      }

      return () => {
        setListRows(ImmList());
        clearAutoRefresh();

        act.deleteResource(_dataPath);
        if (!_.isEmpty(filterNeeds)) {
          filterNeeds.forEach(need => {
            act.deleteResource(need.dataPath);
          });
        }
      };
    }, deps);
  };
  const effectCheckFilter = (deps: React.DependencyList) => {
    useEffect(() => {
      const previousQuery = previousQueryRef.current;
      if (
        (!isFirstFetchDone && !isLoading && filterNeeds.length === 0) ||
        (JSON.stringify(previousQuery) !== JSON.stringify(props.transferPropsRoute.query) && filterNeeds.length > 0)
      ) {
        //First we need to check if there are some router filter
        initAdvancedSearch();
      }

      previousQueryRef.current = props.transferPropsRoute.query;

      checkParamsRouteToFetch();
    }, deps);
  };

  const effectAfterFetch = (deps: React.DependencyList) => {
    useEffect(() => {
      const nextStoreState = props.data.getIn([_dataPath]);

      if (nextStoreState?.toJS().data) {
        const { listRows, total } = getStoreData(props.data);

        if (listRows == null) setListRows(ImmList());
        else {
          const _preprocessedRows = preprocessRow(props.fieldObjectRequest ? listRows.get(props.fieldObjectRequest) : listRows);

          setListRows(_preprocessedRows);
          setState({ pagination: { ...state.pagination, total } });
        }
        setFirstFetchDone(true);
        setLoading(false);
      } else {
        setLoading(false);
      }
    }, deps);
  };

  const effectFetch = (deps: React.DependencyList) => {
    useEffect(() => {
      if (state.isAdvancedSearchInit) {
        setLoading(true);
        if (props.rows) {
          setListRows(preprocessRow(props.rows));
          setLoading(false);
        } else {
          fetchPage();
        }
      }
    }, deps);
  };
  //#endregion

  effectInitTableV3([props.itemsFetchUrl]);
  effectCheckFilter([filterNeeds, props.data, props.transferPropsRoute]);
  effectFetch([
    props.itemsFetchUrl,
    state.isAdvancedSearchInit,
    state.pagination.activePage,
    state.pagination.itemsPerPage,
    state.advancedSearch,
    state.quickfindWord,
    state.sort,
  ]);
  effectAfterFetch([props.data]);

  try {
    const shouldRender403 = render403();
    if (shouldRender403) return shouldRender403;

    return (
      <Grid container sx={{ height: '100%', width: '100%', position: 'relative' }} id="tableV3">
        {renderToolbar()}
        {renderTable()}
        {renderFooterPagination()}
        {renderDeleteConfirmation()}
        {renderAdvancedSearch()}
        {renderTableSettings()}
        {renderPopoverActions()}
      </Grid>
    );
  } catch (error) {
    console.error(error);
    return (
      <div>
        <h1>Oops something went wrong...</h1>
      </div>
    );
  }
};

TableV3.propTypes = {
  componentId: PropTypes.string.isRequired,
  app: PropTypes.object.isRequired,
  aclId: PropTypes.string.isRequired, //For see the permisions

  data: PropTypes.object.isRequired,
  itemsDataPath: PropTypes.string, // store datapath to work if is undefined use componentId
  findAndCount: PropTypes.bool,
  fieldObjectRequest: PropTypes.string, //if fiendAndCount===false and the request return a object we need this

  mode: PropTypes.string, // crud | select | multiselect
  columns: PropTypes.array.isRequired, //All columns to see
  rows: PropTypes.object, //no need fetch
  pkColumn: PropTypes.string.isRequired, // Primary key we need?

  downloadOptions: PropTypes.oneOfType([PropTypes.object, PropTypes.func]), // Function to define the csv to be downloaded
  showFilterDesc: PropTypes.bool, // For see the chips with the filters *no used* (always true)

  //#region Multiselection related props
  selected: PropTypes.object, //No used?
  onChangeSelected: PropTypes.func, //No used?
  //#endregion

  //#region  Query modifiers
  itemsFetchUrl: PropTypes.string.isRequired, // URL for fetch
  crudURL: PropTypes.string, // URL for create /edit / view
  editUrl: PropTypes.string, // URL for edit
  fetchData: PropTypes.object, //Preference for fetch
  where: PropTypes.object, // Object for fetch
  predefinedFilter: PropTypes.object, //{ "filter": "Inventory"} looking for something and add in where
  quickFindInit: PropTypes.string, //Initial state for quick search  (quickFindInitWorld)
  //#endregion

  //#region Advanced search
  initAdvancedSearch: PropTypes.object, //Initial state for advanced search (immutable)
  advancedSearchComponent: PropTypes.oneOfType([PropTypes.object, PropTypes.func]), // React component for advanced search
  onChangeAdvancedSearch: PropTypes.func, // called when advanced search changes from parent
  //#endregion

  //#region EntitySelector
  isEntitySelector: PropTypes.bool, //See if the tableV3 is a EntitySelector
  fnChange: PropTypes.func, // Send in props from EntitySelector
  onChange: PropTypes.func, // Send in props from EntitySelector
  //#endregion

  // Columns selector
  //onConfirmTableSettings: PropTypes.func, //No used
  //columnsDataDatapath: PropTypes.string,  //No used
  //columnsFieldsToShowOnHover: PropTypes.array, //No used

  restrictionEdit: PropTypes.object, //Restriction for edit, example { field: 'isSystem', value: true }
  restrictionDelete: PropTypes.object, //Restriction for delete, example { field: 'isSystem', value: true }

  //CM
  isModal: PropTypes.bool, //See if the tableV3 is in modal
  shouldExtraAction: PropTypes.bool, //To enable the extra action
  navigateId: PropTypes.array, //Array for found the id to crud ex: ['results','locationRuleId']
  confirmDeleteMessage: PropTypes.string, //Message for delete confirmation

  collapsableComponent: PropTypes.any,

  //WE NEED TO REMOVE
  editDataPath: PropTypes.string, //Used in EDITPAGE we need to delete
  defaultCreationProperties: PropTypes.object, //Used in EDITPAGE we need to delete
};

export default WithAllPropsApp(withTranslation()(withRouter(TableV3)));
